import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryOptions } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getSignedThumbs } from "@/lib/photoUrl";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, MapPin, Star, ExternalLink, Pencil, Save, X } from "lucide-react";
import { toast } from "sonner";
import { LANGS, pickDescription, tKind, useT } from "@/lib/i18n";
import type { BusinessKind } from "@/lib/admin-labels";

import { useAuth } from "@/hooks/useAuth";
import {
  getBusinessPublicHead,
  type BusinessPublicHead,
} from "@/lib/business-public.functions";
import { getPublicMenusForBusiness } from "@/lib/menu-public.functions";
import { UtensilsCrossed } from "lucide-react";
import { PublicSuppliersSection } from "@/components/PublicSuppliersSection";
import { ReviewsSection } from "@/components/ReviewsSection";
import { BusinessSubscriptionsCard } from "@/components/BusinessSubscriptionsCard";

const MENU_LABELS: Record<string, { header: string; view: string }> = {
  cs: { header: "Menu", view: "Zobrazit menu" },
  pl: { header: "Menu", view: "Zobacz menu" },
  en: { header: "Menu", view: "View menu" },
  es: { header: "Menú", view: "Ver menú" },
  de: { header: "Speisekarte", view: "Karte ansehen" },
  pt: { header: "Menu", view: "Ver menu" },
  fr: { header: "Menu", view: "Voir le menu" },
  hr: { header: "Jelovnik", view: "Pogledaj jelovnik" },
  sk: { header: "Menu", view: "Zobraziť menu" },
  ro: { header: "Meniu", view: "Vezi meniul" },
  it: { header: "Menu", view: "Vedi menu" },
  hu: { header: "Étlap", view: "Étlap megtekintése" },
  bg: { header: "Меню", view: "Виж менюто" },
};

const businessHeadQuery = (id: string) =>
  queryOptions({
    queryKey: ["business-public-head", id],
    queryFn: () => getBusinessPublicHead({ data: { id } }),
  });

export const Route = createFileRoute("/podnik/$id/")({
  loader: ({ params, context }) =>
    context.queryClient.ensureQueryData(businessHeadQuery(params.id)),
  head: ({ params, loaderData }) => {
    const url = `https://cerveza.international/podnik/${params.id}`;
    if (!loaderData) {
      return {
        meta: [
          { title: "Podnik nenalezen — Cerveza.International" },
          { name: "robots", content: "noindex" },
        ],
        links: [{ rel: "canonical", href: url }],
      };
    }
    const d = loaderData as BusinessPublicHead;
    const placeStr = [d.city, d.country].filter(Boolean).join(", ");
    const title = placeStr
      ? `${d.name}, ${placeStr} — Cerveza.International`
      : `${d.name} — Cerveza.International`;
    const desc =
      (d.description ?? "").trim().slice(0, 200) ||
      [d.kind, placeStr].filter(Boolean).join(" · ") ||
      d.name;
    const meta: Array<Record<string, string>> = [
      { title },
      { name: "description", content: desc },
      { property: "og:title", content: title },
      { property: "og:description", content: desc },
      { property: "og:type", content: "article" },
      { property: "og:url", content: url },
      { property: "og:site_name", content: "Cerveza.International" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: desc },
    ];
    if (d.photo_url) {
      meta.push(
        { property: "og:image", content: d.photo_url },
        { property: "og:image:alt", content: d.name },
        { name: "twitter:image", content: d.photo_url },
        { name: "twitter:image:alt", content: d.name },
      );
    }
    return {
      meta,
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: BusinessProfilePage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="mx-auto max-w-2xl p-8 text-center">
        <h1 className="mb-2 font-serif text-2xl font-bold">Error</h1>
        <p className="mb-4 text-muted-foreground">{error.message}</p>
        <Button onClick={() => { reset(); router.invalidate(); }}>Retry</Button>
      </div>
    );
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl p-8 text-center">
      <h1 className="mb-2 font-serif text-2xl font-bold">Not found</h1>
      <Link to="/mapa" className="text-brand-terracotta underline">← /mapa</Link>
    </div>
  ),
});


type Business = {
  id: string; name: string; kind: BusinessKind; status: string;
  city: string; country: string; postal_code: string | null;
  street: string | null; latitude: number | null; longitude: number | null;
  description: string | null;
  descriptions: Record<string, string> | null;
  owner_id: string | null;
};

type BusinessPhoto = { id: string; storage_path: string };

const EMPTY_PHOTOS: BusinessPhoto[] = [];

const PARTNER_HOSTS = ["directclub.digital", "direct.club"];
function isPartnerUrl(u: string) {
  try { const h = new URL(u).hostname.toLowerCase(); return PARTNER_HOSTS.some((d) => h === d || h.endsWith("." + d)); } catch { return false; }
}

const MAX_DESC_LEN = 2000;

function BusinessProfilePage() {
  const { id } = Route.useParams();
  const { t, lang } = useT();
  const { user, isStaff } = useAuth();
  const qc = useQueryClient();

  const { data: biz, isLoading } = useQuery({
    queryKey: ["business-profile", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("businesses")
        .select("id, name, kind, status, city, country, postal_code, street, latitude, longitude, description, descriptions, owner_id")
        .eq("id", id)
        .maybeSingle();
      if (error) throw error;
      return data as Business | null;
    },
  });

  const canEdit = !!biz && !!user && (isStaff || biz.owner_id === user.id);

  const { data: links = [] } = useQuery({
    queryKey: ["business-profile-links", id],
    queryFn: async () => {
      const nowIso = new Date().toISOString();
      const { data, error } = await supabase
        .from("business_links")
        .select("id, url, label, expires_at, promo_months")
        .eq("business_id", id)
        .eq("status", "active")
        .or(`expires_at.is.null,expires_at.gt.${nowIso}`);
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: photosData } = useQuery({
    queryKey: ["business-profile-photos", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("business_photos")
        .select("id, storage_path")
        .eq("business_id", id)
        .eq("status", "active")
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as BusinessPhoto[];
    },
  });
  const photos = photosData ?? EMPTY_PHOTOS;

  const { data: menus = [] } = useQuery({
    queryKey: ["business-profile-menus", id],
    queryFn: () => getPublicMenusForBusiness({ data: { businessId: id } }),
  });

  const [signed, setSigned] = useState<Record<string, string>>({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (photos.length === 0) {
        setSigned((prev) => (Object.keys(prev).length === 0 ? prev : {}));
        return;
      }
      const out = await getSignedThumbs(photos, { width: 500, height: 500, quality: 70 });
      if (!cancelled) setSigned(out);
    })();
    return () => { cancelled = true; };
  }, [photos]);

  // ---- Edit state ----
  const [editing, setEditing] = useState(false);
  const initialDrafts = useMemo<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const l of LANGS) out[l.code] = biz?.descriptions?.[l.code] ?? "";
    if (!Object.values(out).some(Boolean) && biz?.description) out.cs = biz.description;
    return out;
  }, [biz]);
  const [drafts, setDrafts] = useState<Record<string, string>>(initialDrafts);
  useEffect(() => { setDrafts(initialDrafts); }, [initialDrafts]);

  const saveMut = useMutation({
    mutationFn: async () => {
      const cleaned: Record<string, string> = {};
      for (const [k, v] of Object.entries(drafts)) {
        const s = (v ?? "").trim();
        if (s) cleaned[k] = s.slice(0, MAX_DESC_LEN);
      }
      const { error } = await supabase
        .from("businesses")
        .update({ descriptions: cleaned } as never)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t("profile.descriptionsSaved"));
      setEditing(false);
      qc.invalidateQueries({ queryKey: ["business-profile", id] });
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : t("profile.saveFailed");
      toast.error(msg);
    },
  });

  if (isLoading) {
    return <div className="grid min-h-[60vh] place-items-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }
  if (!biz || (biz.status !== "approved" && !canEdit)) {
    return (
      <div className="mx-auto max-w-2xl p-8 text-center">
        <h1 className="mb-2 font-serif text-2xl font-bold">{t("profile.notFound")}</h1>
        <Link to="/mapa" className="text-brand-terracotta underline">{t("profile.backToMap")}</Link>
      </div>
    );
  }

  const isPartner = links.some((l) => isPartnerUrl(l.url));
  const address = [biz.street, `${biz.postal_code ?? ""} ${biz.city}`.trim(), biz.country].filter(Boolean).join(", ");

  return (
    <div className="min-h-screen bg-brand-cream">
      <div className="mx-auto max-w-4xl px-3 py-6 sm:px-6 sm:py-8">

        <Card className="overflow-hidden">
          {photos.length > 0 && (
            <div className="grid grid-cols-2 gap-1 md:grid-cols-3">
              {photos.map((p) => signed[p.id] ? (
                <img
                  key={p.id}
                  src={signed[p.id]}
                  alt={biz.name}
                  loading="lazy"
                  decoding="async"
                  width={500}
                  height={500}
                  className="aspect-square w-full object-cover"
                />
              ) : (
                <div key={p.id} className="aspect-square w-full bg-muted" />
              ))}
            </div>
          )}

          <div className="p-4 sm:p-6">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <h1 className="font-serif text-2xl font-bold text-brand-earth sm:text-3xl">{biz.name}</h1>
              {isPartner && (
                <Badge className="bg-amber-400 text-amber-900 hover:bg-amber-400">
                  <Star className="mr-1 h-3 w-3 fill-current" /> Direct Club Partner
                </Badge>
              )}
              {biz.status !== "approved" && (
                <Badge variant="outline">{biz.status}</Badge>
              )}
            </div>

            <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <Badge variant="secondary">{tKind(t, biz.kind)}</Badge>
              <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {address}</span>
            </div>

            {/* Descriptions */}
            <div className="mb-6">
              {!editing ? (
                <>
                  {(() => {
                    const desc = pickDescription(lang, biz.descriptions, biz.description);
                    return desc ? (
                      <p className="whitespace-pre-line text-foreground/90">{desc}</p>
                    ) : (
                      <p className="text-sm italic text-muted-foreground">
                        {canEdit ? t("profile.noDescription") : ""}
                      </p>
                    );
                  })()}
                  {canEdit && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={() => setEditing(true)}
                    >
                      <Pencil className="mr-2 h-3.5 w-3.5" />
                      {t("profile.editDescription")}
                    </Button>
                  )}
                </>
              ) : (
                <div className="space-y-3 rounded-lg border border-brand-earth/15 bg-card/50 p-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-brand-earth">{t("profile.multiLangDesc")}</h2>
                    <span className="text-xs text-muted-foreground">{t("profile.maxChars", { n: MAX_DESC_LEN })}</span>
                  </div>
                  {LANGS.map((l) => (
                    <div key={l.code}>
                      <label className="mb-1 block text-xs font-medium text-muted-foreground">
                        {l.label}
                      </label>
                      <Textarea
                        rows={3}
                        maxLength={MAX_DESC_LEN}
                        value={drafts[l.code] ?? ""}
                        onChange={(e) =>
                          setDrafts((d) => ({ ...d, [l.code]: e.target.value }))
                        }
                        placeholder={t("profile.descPlaceholder", { lang: l.label })}
                      />
                    </div>
                  ))}
                  <div className="flex flex-wrap gap-2 pt-2">
                    <Button
                      type="button"
                      onClick={() => saveMut.mutate()}
                      disabled={saveMut.isPending}
                    >
                      {saveMut.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="mr-2 h-4 w-4" />
                      )}
                      {t("profile.save")}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => { setDrafts(initialDrafts); setEditing(false); }}
                      disabled={saveMut.isPending}
                    >
                      <X className="mr-2 h-4 w-4" /> {t("profile.cancel")}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {menus.length > 0 && (
              <div className="mb-6 space-y-2">
                <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {(MENU_LABELS[lang] ?? MENU_LABELS.en).header}
                </h2>
                <div className="grid gap-2 sm:grid-cols-2">
                  {menus.map((m: any) => (
                    <Link
                      key={m.id}
                      to="/podnik/$id/menu/$menuSlug"
                      params={{ id, menuSlug: m.slug }}
                      className="flex items-center gap-3 rounded-md border border-brand-earth/10 bg-card px-3 py-2 text-sm transition hover:border-brand-terracotta/40"
                    >
                      <UtensilsCrossed className="h-4 w-4 flex-shrink-0 text-brand-terracotta" />
                      <span className="flex-1 truncate font-medium">{m.public_name || m.internal_name}</span>
                      <span className="text-xs text-muted-foreground">
                        {(MENU_LABELS[lang] ?? MENU_LABELS.en).view} →
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {canEdit && <BusinessSubscriptionsCard businessId={id} lang={lang} />}

            <PublicSuppliersSection businessId={id} lang={lang} />

            <ReviewsSection targetType="business" targetId={id} urlKey="rev" />

            {links.length > 0 && (
              <div className="space-y-2">
                <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("profile.linksHeader")}</h2>
                {links.map((l) => (
                  <a key={l.id} href={l.url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-md border border-brand-earth/10 bg-card px-3 py-2 text-sm transition hover:border-brand-terracotta/40">
                    <ExternalLink className="h-4 w-4 text-brand-terracotta" />
                    <span className="flex-1 truncate">{l.label || l.url}</span>
                    {isPartnerUrl(l.url) && <Badge className="bg-amber-400 text-amber-900 hover:bg-amber-400 text-xs">★</Badge>}
                    {l.promo_months && l.promo_months > 0 && l.expires_at && (
                      <Badge className="bg-emerald-600 text-white hover:bg-emerald-600 text-xs whitespace-nowrap">
                        {t("link.promo", { months: l.promo_months, date: new Date(l.expires_at).toLocaleDateString(lang) })}
                      </Badge>
                    )}
                  </a>
                ))}
              </div>
            )}

            {biz.latitude && biz.longitude && (
              <div className="mt-6">
                <Button variant="outline" asChild>
                  <a href={`https://www.openstreetmap.org/?mlat=${biz.latitude}&mlon=${biz.longitude}#map=17/${biz.latitude}/${biz.longitude}`} target="_blank" rel="noopener noreferrer">
                    {t("profile.viewOnOSM")}
                  </a>
                </Button>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
